import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {dataResult, type Result} from "@vesta/error-handling-remix/result";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {plutoFetchPdfBlob, plutoPost, type PlutoWorkspaceClientContext} from "~/utilities/ottokart/plutoClient";
import {inclusiveIsoDateTimeToExclusiveIsoDateTime} from "~/utilities/calendarDateIso";
import {
    buildPlutoCreateBodyFromLegacyPayload,
    buildPlutoReviseBodyFromLegacyPayload,
    filterPlutoQuotationsForModule,
    isPlutoQuotationAdvanced,
    mapPlutoQuotationStatusToAdminStatus,
    mapPlutoQuotationToAdminQuotation,
    mapPlutoQuotationToAdvancedQuotation,
    plutoQuotationWithLineItemsZodValidator,
    plutoQuotationZodValidator,
    type LegacyQuotationWritePayload,
    type PlutoQuotation,
    type PlutoQuotationLineItem,
} from "~/utilities/ottokart/plutoQuotationMappers";
import {getAllWorkspaceUserDetails} from "~/utilities/queries";
import type {ZodType} from "zod";
import {z as zod} from "zod";

const PLUTO_QUOTATIONS_PAGE_SIZE = 100;

async function loadWorkspaceUserEmailById({
    accessToken,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext): Promise<Map<Uuid, string>> {
    const userDetailsResult = await getAllWorkspaceUserDetails({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });
    const emailByUserId = new Map<Uuid, string>();
    if (userDetailsResult.success !== true) {
        return emailByUserId;
    }

    for (const user of userDetailsResult.data) {
        const email = user.email?.trim() ?? "";
        if (email.length > 0) {
            emailByUserId.set(user.userId, email);
            continue;
        }
        const name = user.name?.trim() ?? "";
        if (name.length > 0) {
            emailByUserId.set(user.userId, name);
        }
    }

    return emailByUserId;
}

function attachQuotationActorEmails<
    TQuotation extends {
        generatedBy: Uuid | null;
        generatedByEmail: string | null;
        sharedBy: Uuid | null;
        sharedByEmail: string | null;
    },
>(quotation: TQuotation, emailByUserId: Map<Uuid, string>): TQuotation {
    return {
        ...quotation,
        generatedByEmail:
            quotation.generatedBy === null ? null : (emailByUserId.get(quotation.generatedBy) ?? null),
        sharedByEmail: quotation.sharedBy === null ? null : (emailByUserId.get(quotation.sharedBy) ?? null),
    };
}

async function fetchAllPlutoQuotations({
    accessToken,
    latestRevisionOnly = true,
    intellsysBaseUrl,
    pageSize = PLUTO_QUOTATIONS_PAGE_SIZE,
    profileId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    latestRevisionOnly?: boolean;
    pageSize?: number;
    profileId?: Uuid;
}): Promise<Result<Array<{lineItems: Array<PlutoQuotationLineItem>; quotation: PlutoQuotation}>>> {
    const safePageSize = Math.max(1, pageSize);
    const quotations: Array<{lineItems: Array<PlutoQuotationLineItem>; quotation: PlutoQuotation}> = [];
    let offset = 0;

    while (true) {
        const pageResult = await plutoPost({
            accessToken: accessToken,
            intellsysBaseUrl: intellsysBaseUrl,
            workspaceId: workspaceId,
            path: "/quotations/get-all",
            body: {
                latestRevisionOnly: latestRevisionOnly,
                limit: safePageSize,
                offset: offset,
                ...(profileId === undefined
                    ? {}
                    : {
                          profileId: profileId,
                      }),
            },
            responseValidator: zod.array(plutoQuotationWithLineItemsZodValidator),
        });
        if (pageResult.success !== true) {
            return pageResult;
        }

        quotations.push(...pageResult.data);
        if (pageResult.data.length < safePageSize) {
            break;
        }
        offset += safePageSize;
    }

    return dataResult(quotations);
}

const plutoQuotationPageRowZodValidator = plutoQuotationWithLineItemsZodValidator.extend({
    revisionCount: zod.coerce.number().int().nonnegative().optional(),
});

async function fetchQuotationModulePage(args: PlutoWorkspaceClientContext & {
    filters?: QuotationListServerFilters;
    generatedBy?: Uuid;
    kanbanColumn?: QuotationListKanbanColumn;
    limit: number;
    module: "standard";
    offset: number;
    search?: string;
}): Promise<Result<Array<Quotation>>>;
async function fetchQuotationModulePage(args: PlutoWorkspaceClientContext & {
    filters?: QuotationListServerFilters;
    generatedBy?: Uuid;
    kanbanColumn?: QuotationListKanbanColumn;
    limit: number;
    module: "advanced";
    offset: number;
    search?: string;
}): Promise<Result<Array<AdvancedQuotation>>>;
async function fetchQuotationModulePage({
    accessToken,
    filters,
    generatedBy,
    intellsysBaseUrl,
    kanbanColumn,
    limit,
    module,
    offset,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: QuotationListServerFilters;
    generatedBy?: Uuid;
    kanbanColumn?: QuotationListKanbanColumn;
    limit: number;
    module: "advanced" | "standard";
    offset: number;
    search?: string;
}): Promise<Result<Array<Quotation | AdvancedQuotation>>> {
    const safeLimit = Math.max(1, limit);
    const safeOffset = Math.max(0, offset);
    const trimmedSearch = search?.trim() ?? "";
    const pageResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/get-all",
        body: {
            isAdvanced: module === "advanced",
            latestRevisionOnly: true,
            limit: safeLimit,
            offset: safeOffset,
            ...(generatedBy !== undefined ? {generatedBy: generatedBy} : {}),
            ...(kanbanColumn !== undefined ? {kanbanColumn: kanbanColumn} : {}),
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...quotationFiltersBody(filters),
        },
        responseValidator: zod.array(plutoQuotationPageRowZodValidator),
    });
    if (pageResult.success !== true) {
        return pageResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    return dataResult(
        filterPlutoQuotationsForModule(pageResult.data, module).map((plutoQuotationWithLineItems) => {
            const mapped = module === "advanced"
                ? mapPlutoQuotationToAdvancedQuotation({
                    lineItems: plutoQuotationWithLineItems.lineItems,
                    plutoQuotation: plutoQuotationWithLineItems.quotation,
                })
                : mapPlutoQuotationToAdminQuotation({
                    lineItems: plutoQuotationWithLineItems.lineItems,
                    plutoQuotation: plutoQuotationWithLineItems.quotation,
                });
            const withEmails = attachQuotationActorEmails(mapped, emailByUserId);
            if (plutoQuotationWithLineItems.revisionCount === undefined) {
                return withEmails;
            }
            return {
                ...withEmails,
                revisionCount: plutoQuotationWithLineItems.revisionCount,
            };
        }),
    );
}

export type QuotationListKanbanColumn = "requested" | "sent" | "expiring-soon" | "expired";

export type QuotationListServerFilters = {
    company?: Array<string>;
    createdAtFrom?: string | null;
    createdAtTo?: string | null;
    phone?: Array<string>;
    requestedBy?: Array<string>;
};

export type QuotationListServerFilterColumn = "company" | "createdAt" | "phone" | "requestedBy";

export function quotationListServerFiltersKey(filters: QuotationListServerFilters): string {
    const valuesKey = (values: Array<string> | undefined): string => {
        if (values === undefined) {
            return "*";
        }
        return values.join("\0");
    };
    return [
        valuesKey(filters.company),
        valuesKey(filters.phone),
        valuesKey(filters.requestedBy),
        filters.createdAtFrom ?? "",
        filters.createdAtTo ?? "",
    ].join("|");
}

function quotationFiltersBody(filters: QuotationListServerFilters | undefined): {filters: QuotationListServerFilters} | Record<string, never> {
    if (filters === undefined) {
        return {};
    }
    const requestFilters: QuotationListServerFilters = {};
    if (filters.company !== undefined) {
        requestFilters.company = filters.company;
    }
    if (filters.phone !== undefined) {
        requestFilters.phone = filters.phone;
    }
    if (filters.requestedBy !== undefined) {
        requestFilters.requestedBy = filters.requestedBy;
    }
    if (filters.createdAtFrom !== undefined && filters.createdAtTo !== undefined) {
        requestFilters.createdAtFrom = filters.createdAtFrom;
        requestFilters.createdAtTo = filters.createdAtTo;
    }
    if (
        requestFilters.company === undefined &&
        requestFilters.phone === undefined &&
        requestFilters.requestedBy === undefined &&
        requestFilters.createdAtFrom === undefined &&
        requestFilters.createdAtTo === undefined
    ) {
        return {};
    }
    return {
        filters: requestFilters,
    };
}

export async function listQuotationColumnValues({
    accessToken,
    column,
    intellsysBaseUrl,
    module,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    column: Exclude<QuotationListServerFilterColumn, "createdAt">;
    module: "advanced" | "standard";
}): Promise<Result<Array<string>>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/get-all",
        body: {
            columnValuesFor: column,
            isAdvanced: module === "advanced",
            latestRevisionOnly: true,
        },
        responseValidator: zod.array(zod.string()),
    });
}

export async function countQuotations({
    accessToken,
    filters,
    generatedBy,
    intellsysBaseUrl,
    kanbanColumn,
    module,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: QuotationListServerFilters;
    generatedBy?: Uuid;
    kanbanColumn?: QuotationListKanbanColumn;
    module: "advanced" | "standard";
    search?: string;
}): Promise<Result<number>> {
    const trimmedSearch = search?.trim() ?? "";
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/get-all",
        body: {
            countOnly: true,
            isAdvanced: module === "advanced",
            latestRevisionOnly: true,
            ...(generatedBy !== undefined ? {generatedBy: generatedBy} : {}),
            ...(kanbanColumn !== undefined ? {kanbanColumn: kanbanColumn} : {}),
            ...(trimmedSearch.length > 0 ? {search: trimmedSearch} : {}),
            ...quotationFiltersBody(filters),
        },
        responseValidator: zod.coerce.number().int().nonnegative(),
    });
}

function triggerPdfBlobDownload(blob: Blob, quotationId: string, disposition: string | null): void {
    const filenameMatch = disposition?.match(/filename="?([^";\n]+)"?/);
    const filename = filenameMatch?.[1] ?? `quotation-${quotationId}.pdf`;
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = filename;
    anchor.style.display = "none";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
}
// --- Types (QuotationModel) ---

export type QuotationItem = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    quotationId: Uuid;
    variantId: Uuid;
    /** Snapshot SKU when present on the Pluto line item (`sku_override`). */
    sku: string | null;
    sellingPrice: number;
    marketPrice: number;
    quantity: number;
    discount: number | null;
    compatibleMachines: string[];
};

/** Advanced quotation line item (`advanced_quotation_items` table). */
export type AdvancedQuotationItem = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    quotationId: Uuid;
    variantId: Uuid | null;
    productName: string;
    categoryName: string | null;
    sku: string | null;
    sortOrder: number;
    isCustom: boolean;
    sellingPrice: number;
    marketPrice: number;
    quantity: number;
    discount: number | null;
    compatibleMachines: string[];
};

export type AdvancedQuotation = Omit<Quotation, "items"> & {
    items: Array<AdvancedQuotationItem>;
};

export type Quotation = {
    id: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    createdBy: Uuid;
    updatedBy: Uuid;
    generatedBy: Uuid | null;
    generatedByEmail: string | null;
    sharedBy: Uuid | null;
    sharedByEmail: string | null;
    slug: string;
    customerId: Uuid;
    status: Uuid;
    discountType: Uuid | null;
    discountValue: number | null;
    taxRate: number;

    billingDetailsCompanyName: string;
    billingDetailsGstin: string | null;
    billingDetailsPhoneNumber: string;
    billingDetailsEmail: string;
    billingDetailsAddress: string;
    billingDetailsState: string;
    billingDetailsCity: string;
    billingDetailsPinCode: string;

    shippingDetailsCompanyName: string;
    shippingDetailsGstin: string | null;
    shippingDetailsPhoneNumber: string;
    shippingDetailsEmail: string;
    shippingDetailsAddress: string;
    shippingDetailsState: string;
    shippingDetailsCity: string;
    shippingDetailsPinCode: string;

    isBillingSameAsShipping: boolean;

    sourceQuotationId: Uuid;

    /** Pluto b2bQuotationEngine status UUID — used for RFQ approve/reject and kanban. */
    plutoStatus: string;
    validUntil: Iso8601DateTime | null;

    termsAndConditions: string[] | null;

    contactNumber: string;
    emailId: string;
    incoTerms: string;
    termsOfPayment: string;
    customerReferenceNumber: string;
    customerReferenceDate: string;
    consigneeShipTo: string;
    buyerBillTo: string;

    freightCharges: number | null;

    items: Array<QuotationItem>;

    /** Revision count for this family when the row came from a paged list request. */
    revisionCount?: number;

    /**
     * When present, marks whether this row belongs to the advanced quotations module.
     * Pluto stores both modules in one table; customer views need this for routing.
     */
    isAdvanced?: boolean;
};

export type QuotationVersion = {
    id: Uuid;
    slug: string;
    status: Uuid;
    createdAt: Iso8601DateTime;
    updatedAt: Iso8601DateTime;
    sourceQuotationId: Uuid;
};

// --- PostQuotation (create-quotation body; id/createdAt/updatedAt set by backend) ---

export type PostQuotationItem = {
    id?: Uuid;
    variantId: Uuid;
    productName?: string;
    sku?: string | null;
    sortOrder?: number;
    isCustom?: boolean;
    sellingPrice: number;
    marketPrice: number;
    quantity: number;
    discount?: number | null;
    compatibleMachines?: string[];
};

export type PostAdvancedQuotationItem = {
    id?: Uuid;
    variantId?: Uuid | null;
    productName: string;
    categoryName?: string | null;
    sku?: string | null;
    sortOrder: number;
    isCustom: boolean;
    sellingPrice: number;
    marketPrice: number;
    quantity: number;
    discount?: number | null;
    compatibleMachines?: string[];
};

export type PostAdvancedQuotation = Omit<PostQuotation, "items"> & {
    items: Array<PostAdvancedQuotationItem>;
};

export type PostQuotation = {
    createdBy: Uuid;
    updatedBy?: Uuid;
    customerId: Uuid;
    status: Uuid;
    discountType?: Uuid | null;
    discountValue?: number | null;
    taxRate: number;

    billingDetailsCompanyName: string;
    billingDetailsGstin?: string | null;
    billingDetailsEmail: string;
    billingDetailsAddress: string;
    billingDetailsState: string;
    billingDetailsCity: string;
    billingDetailsPinCode: string;
    billingDetailsPhoneNumber: string;

    shippingDetailsCompanyName: string;
    shippingDetailsGstin?: string | null;
    shippingDetailsEmail: string;
    shippingDetailsAddress: string;
    shippingDetailsState: string;
    shippingDetailsCity: string;
    shippingDetailsPinCode: string;
    shippingDetailsPhoneNumber: string;

    isBillingSameAsShipping: boolean;

    items: Array<PostQuotationItem>;
};

// --- Zod validators (core data only; robustJsonResultClientFetch unwraps success/data) ---

/** API may return `compatibleMachines: null`; normalize to `[]` for app use. */
const compatibleMachinesListZodValidator = zod.preprocess((value) => (value === null || value === undefined ? [] : value), zod.array(zod.string()));

export const quotationItemZodValidator: ZodType<QuotationItem> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    quotationId: uuidZodValidator,
    variantId: uuidZodValidator,
    sku: zod.string().nullable(),
    sellingPrice: zod.number(),
    marketPrice: zod.number(),
    quantity: zod.number(),
    discount: zod.number().nullable(),
    compatibleMachines: compatibleMachinesListZodValidator,
});

export const quotationZodValidator: ZodType<Quotation> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    generatedBy: uuidZodValidator.nullable(),
    generatedByEmail: zod.string().nullable(),
    sharedBy: uuidZodValidator.nullable(),
    sharedByEmail: zod.string().nullable(),
    slug: zod.string(),
    customerId: uuidZodValidator,
    status: uuidZodValidator,
    discountType: uuidZodValidator.nullable(),
    discountValue: zod.number().nullable(),
    taxRate: zod.number(),

    billingDetailsCompanyName: zod.string(),
    billingDetailsGstin: zod.string().nullable(),
    billingDetailsPhoneNumber: zod.string(),
    billingDetailsEmail: zod.string(),
    billingDetailsAddress: zod.string(),
    billingDetailsState: zod.string(),
    billingDetailsCity: zod.string(),
    billingDetailsPinCode: zod.string(),

    shippingDetailsCompanyName: zod.string(),
    shippingDetailsGstin: zod.string().nullable(),
    shippingDetailsPhoneNumber: zod.string(),
    shippingDetailsEmail: zod.string(),
    shippingDetailsAddress: zod.string(),
    shippingDetailsState: zod.string(),
    shippingDetailsCity: zod.string(),
    shippingDetailsPinCode: zod.string(),

    isBillingSameAsShipping: zod.boolean(),

    sourceQuotationId: uuidZodValidator,

    plutoStatus: zod.string(),
    validUntil: iso8601DateTimeZodValidator.nullable(),

    termsAndConditions: zod.array(zod.string()).nullable(),

    contactNumber: zod.string(),
    emailId: zod.string(),
    incoTerms: zod.string(),
    termsOfPayment: zod.string(),
    customerReferenceNumber: zod.string(),
    customerReferenceDate: zod.string(),
    consigneeShipTo: zod.string(),
    buyerBillTo: zod.string(),

    freightCharges: zod.number().nullable(),

    items: zod.array(quotationItemZodValidator),
});

export const quotationVersionZodValidator: ZodType<QuotationVersion> = zod.object({
    id: uuidZodValidator,
    slug: zod.string(),
    status: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    sourceQuotationId: uuidZodValidator,
});

export const postQuotationItemZodValidator: ZodType<PostQuotationItem> = zod.object({
    id: uuidZodValidator.optional(),
    variantId: uuidZodValidator,
    sellingPrice: zod.number(),
    marketPrice: zod.number(),
    quantity: zod.number(),
    discount: zod.number().nullable().optional(),
    compatibleMachines: zod.array(zod.string()).nullable().optional(),
});

export const advancedQuotationItemZodValidator: ZodType<AdvancedQuotationItem> = zod.object({
    id: uuidZodValidator,
    createdAt: iso8601DateTimeZodValidator,
    updatedAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator,
    quotationId: uuidZodValidator,
    variantId: uuidZodValidator.nullable(),
    productName: zod.string(),
    categoryName: zod.string().nullable(),
    sku: zod.string().nullable(),
    sortOrder: zod.number(),
    isCustom: zod.boolean(),
    sellingPrice: zod.number(),
    marketPrice: zod.number(),
    quantity: zod.number(),
    discount: zod.number().nullable(),
    compatibleMachines: compatibleMachinesListZodValidator,
});

export const advancedQuotationZodValidator: ZodType<AdvancedQuotation> = (quotationZodValidator as zod.ZodObject<zod.ZodRawShape>).extend({
    items: zod.array(advancedQuotationItemZodValidator),
}) as ZodType<AdvancedQuotation>;

export const postAdvancedQuotationItemZodValidator: ZodType<PostAdvancedQuotationItem> = zod.object({
    id: uuidZodValidator.optional(),
    variantId: uuidZodValidator.nullable().optional(),
    productName: zod.string(),
    categoryName: zod.string().nullable().optional(),
    sku: zod.string().nullable().optional(),
    sortOrder: zod.number(),
    isCustom: zod.boolean(),
    sellingPrice: zod.number(),
    marketPrice: zod.number(),
    quantity: zod.number(),
    discount: zod.number().nullable().optional(),
    compatibleMachines: zod.array(zod.string()).nullable().optional(),
});

export const postQuotationZodValidator: ZodType<PostQuotation> = zod.object({
    createdBy: uuidZodValidator,
    updatedBy: uuidZodValidator.optional(),
    customerId: uuidZodValidator,
    status: uuidZodValidator,
    discountType: uuidZodValidator.nullable().optional(),
    discountValue: zod.number().nullable().optional(),
    taxRate: zod.number(),

    billingDetailsCompanyName: zod.string(),
    billingDetailsGstin: zod.string().nullable().optional(),
    billingDetailsEmail: zod.string(),
    billingDetailsAddress: zod.string(),
    billingDetailsState: zod.string(),
    billingDetailsCity: zod.string(),
    billingDetailsPinCode: zod.string(),
    billingDetailsPhoneNumber: zod.string(),

    shippingDetailsCompanyName: zod.string(),
    shippingDetailsGstin: zod.string().nullable().optional(),
    shippingDetailsEmail: zod.string(),
    shippingDetailsAddress: zod.string(),
    shippingDetailsState: zod.string(),
    shippingDetailsCity: zod.string(),
    shippingDetailsPinCode: zod.string(),
    shippingDetailsPhoneNumber: zod.string(),

    isBillingSameAsShipping: zod.boolean(),

    items: zod.array(postQuotationItemZodValidator),
});

// --- API calls ---

export async function listQuotations({
    accessToken,
    filters,
    generatedBy,
    latestRevisionOnly = false,
    intellsysBaseUrl,
    kanbanColumn,
    offset,
    pageSize,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: QuotationListServerFilters;
    generatedBy?: Uuid;
    latestRevisionOnly?: boolean;
    kanbanColumn?: QuotationListKanbanColumn;
    offset?: number;
    pageSize?: number;
    search?: string;
}): Promise<Result<Array<Quotation>>> {
    if (offset !== undefined && pageSize !== undefined) {
        return fetchQuotationModulePage({
            accessToken: accessToken,
            filters: filters,
            generatedBy: generatedBy,
            intellsysBaseUrl: intellsysBaseUrl,
            kanbanColumn: kanbanColumn,
            limit: pageSize,
            module: "standard",
            offset: offset,
            search: search,
            workspaceId: workspaceId,
        });
    }

    const plutoQuotationsResult = await fetchAllPlutoQuotations({
        accessToken: accessToken,
        latestRevisionOnly: latestRevisionOnly,
        intellsysBaseUrl: intellsysBaseUrl,
        pageSize: pageSize,
        workspaceId: workspaceId,
    });
    if (plutoQuotationsResult.success !== true) {
        return plutoQuotationsResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    return dataResult(
        filterPlutoQuotationsForModule(plutoQuotationsResult.data, "standard").map((plutoQuotationWithLineItems) => {
            return attachQuotationActorEmails(
                mapPlutoQuotationToAdminQuotation({
                    lineItems: plutoQuotationWithLineItems.lineItems,
                    plutoQuotation: plutoQuotationWithLineItems.quotation,
                }),
                emailByUserId,
            );
        }),
    );
}

export async function listQuotationsByCustomerId({
    accessToken,
    customerId,
    latestRevisionOnly = true,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    customerId: Uuid;
    latestRevisionOnly?: boolean;
}): Promise<Result<Array<Quotation>>> {
    const plutoQuotationsResult = await fetchAllPlutoQuotations({
        accessToken: accessToken,
        latestRevisionOnly: latestRevisionOnly,
        intellsysBaseUrl: intellsysBaseUrl,
        profileId: customerId,
        workspaceId: workspaceId,
    });
    if (plutoQuotationsResult.success !== true) {
        return plutoQuotationsResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    // Customer table aggregates count both modules; list must include both too.
    return dataResult(
        plutoQuotationsResult.data.map((plutoQuotationWithLineItems) => {
            const isAdvanced = isPlutoQuotationAdvanced({
                lineItems: plutoQuotationWithLineItems.lineItems,
                plutoQuotation: plutoQuotationWithLineItems.quotation,
            });
            const mapped = isAdvanced
                ? mapPlutoQuotationToAdvancedQuotation({
                    lineItems: plutoQuotationWithLineItems.lineItems,
                    plutoQuotation: plutoQuotationWithLineItems.quotation,
                })
                : mapPlutoQuotationToAdminQuotation({
                    lineItems: plutoQuotationWithLineItems.lineItems,
                    plutoQuotation: plutoQuotationWithLineItems.quotation,
                });
            return {
                ...attachQuotationActorEmails(mapped as Quotation, emailByUserId),
                isAdvanced: isAdvanced,
            };
        }),
    );
}

export async function getQuotationsAnalyticsSummary({
    accessToken,
    from,
    intellsysBaseUrl,
    to,
    workspaceId,
}: PlutoWorkspaceClientContext & {from: Iso8601DateTime; to: Iso8601DateTime}): Promise<Result<{
    averageQuotationValue: number;
    totalQuotationsCount: number;
    totalQuotationValue: number;
}>> {
    const toExclusive = inclusiveIsoDateTimeToExclusiveIsoDateTime(to);
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/analytics/summary",
        body: {from: from, to: toExclusive},
        responseValidator: zod.object({
            averageQuotationValue: zod.number(),
            totalQuotationsCount: zod.number(),
            totalQuotationValue: zod.number(),
        }),
    });
}

export async function getQuotationsAnalyticsTrend({
    accessToken,
    from,
    intellsysBaseUrl,
    to,
    workspaceId,
}: PlutoWorkspaceClientContext & {from: Iso8601DateTime; to: Iso8601DateTime}): Promise<Result<Array<{
    averageQuotationsAmount: number;
    date: string;
    quotationsCount: number;
    totalQuotationsAmount: number;
}>>> {
    const toExclusive = inclusiveIsoDateTimeToExclusiveIsoDateTime(to);
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/analytics/trend",
        body: {from: from, to: toExclusive},
        responseValidator: zod.array(zod.object({
            averageQuotationsAmount: zod.number(),
            date: zod.string(),
            quotationsCount: zod.number(),
            totalQuotationsAmount: zod.number(),
        })),
    });
}

export async function getActiveCustomersAnalyticsTrend({
    accessToken,
    from,
    intellsysBaseUrl,
    to,
    workspaceId,
}: PlutoWorkspaceClientContext & {from: Iso8601DateTime; to: Iso8601DateTime}): Promise<Result<Array<{
    count: number;
    date: string;
}>>> {
    const toExclusive = inclusiveIsoDateTimeToExclusiveIsoDateTime(to);
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/analytics/active-customers-trend",
        body: {from: from, to: toExclusive},
        responseValidator: zod.array(zod.object({
            count: zod.number(),
            date: zod.string(),
        })),
    });
}

export async function listQuotationVersionsById({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<Array<QuotationVersion>>> {
    const versionsResult = await listQuotationVersionsAsQuotations({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotationId: quotationId,
        workspaceId: workspaceId,
    });
    if (versionsResult.success !== true) {
        return versionsResult;
    }

    return dataResult(
        versionsResult.data.map((quotation) => {
            return {
                id: quotation.id,
                slug: quotation.slug,
                status: quotation.status,
                createdAt: quotation.createdAt,
                updatedAt: quotation.updatedAt,
                sourceQuotationId: quotation.sourceQuotationId,
            };
        }),
    );
}

export async function listQuotationVersionsAsQuotations({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<Array<Quotation>>> {
    const versionsResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/list-versions",
        body: {
            id: quotationId,
        },
        responseValidator: zod.array(plutoQuotationZodValidator),
    });
    if (versionsResult.success !== true) {
        return versionsResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    return dataResult(
        versionsResult.data.map((plutoQuotation) => {
            return attachQuotationActorEmails(
                mapPlutoQuotationToAdminQuotation({
                    plutoQuotation: plutoQuotation,
                }),
                emailByUserId,
            );
        }),
    );
}

export async function getQuotationById({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<Quotation>> {
    const plutoResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/get",
        body: {
            id: quotationId,
        },
        responseValidator: plutoQuotationWithLineItemsZodValidator,
    });
    if (plutoResult.success !== true) {
        return plutoResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    return dataResult(
        attachQuotationActorEmails(
            mapPlutoQuotationToAdminQuotation({
                lineItems: plutoResult.data.lineItems,
                plutoQuotation: plutoResult.data.quotation,
            }),
            emailByUserId,
        ),
    );
}

export type UpdateOttokartQuotationRequest =
    | {
          action: "approve";
          adminNotes?: string | null;
          id: Uuid;
          lineItems: Array<{
              approvedPrice: number;
              id: Uuid;
          }>;
      }
    | {
          action: "reject";
          adminNotes?: string | null;
          id: Uuid;
          rejectionReason: string;
      };

export async function updateOttokartQuotation({
    accessToken,
    intellsysBaseUrl,
    request,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    request: UpdateOttokartQuotationRequest;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/update",
        body: request,
        responseValidator: uuidZodValidator,
    });
}

export async function createQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/create",
        body: buildPlutoCreateBodyFromLegacyPayload(quotation, {
            isAdvanced: false,
        }),
        responseValidator: uuidZodValidator,
    });
}

export async function createNewQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return createQuotation({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotation: quotation,
        workspaceId: workspaceId,
    });
}

export async function createNewAdvancedQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/create",
        body: buildPlutoCreateBodyFromLegacyPayload(quotation, {
            isAdvanced: true,
        }),
        responseValidator: uuidZodValidator,
    });
}

export async function reviseQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/revise",
        body: buildPlutoReviseBodyFromLegacyPayload(quotation, {
            isAdvanced: false,
        }),
        responseValidator: uuidZodValidator,
    });
}

export async function duplicateQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/duplicate",
        body: buildPlutoCreateBodyFromLegacyPayload(quotation, {
            isAdvanced: false,
        }),
        responseValidator: uuidZodValidator,
    });
}

export async function duplicateAdvancedQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/duplicate",
        body: buildPlutoCreateBodyFromLegacyPayload(quotation, {
            isAdvanced: true,
        }),
        responseValidator: uuidZodValidator,
    });
}

export async function listAdvancedQuotations({
    accessToken,
    filters,
    generatedBy,
    latestRevisionOnly = false,
    intellsysBaseUrl,
    kanbanColumn,
    offset,
    pageSize,
    search,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    filters?: QuotationListServerFilters;
    generatedBy?: Uuid;
    latestRevisionOnly?: boolean;
    kanbanColumn?: QuotationListKanbanColumn;
    offset?: number;
    pageSize?: number;
    search?: string;
}): Promise<Result<Array<AdvancedQuotation>>> {
    if (offset !== undefined && pageSize !== undefined) {
        const pageResult = await fetchQuotationModulePage({
            accessToken: accessToken,
            filters: filters,
            generatedBy: generatedBy,
            intellsysBaseUrl: intellsysBaseUrl,
            kanbanColumn: kanbanColumn,
            limit: pageSize,
            module: "advanced",
            offset: offset,
            search: search,
            workspaceId: workspaceId,
        });
        if (pageResult.success !== true) {
            return pageResult;
        }
        return dataResult(pageResult.data);
    }

    const plutoQuotationsResult = await fetchAllPlutoQuotations({
        accessToken: accessToken,
        latestRevisionOnly: latestRevisionOnly,
        intellsysBaseUrl: intellsysBaseUrl,
        pageSize: pageSize,
        workspaceId: workspaceId,
    });
    if (plutoQuotationsResult.success !== true) {
        return plutoQuotationsResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    return dataResult(
        filterPlutoQuotationsForModule(plutoQuotationsResult.data, "advanced").map((plutoQuotationWithLineItems) => {
            return attachQuotationActorEmails(
                mapPlutoQuotationToAdvancedQuotation({
                    lineItems: plutoQuotationWithLineItems.lineItems,
                    plutoQuotation: plutoQuotationWithLineItems.quotation,
                }),
                emailByUserId,
            );
        }),
    );
}

export async function listAdvancedQuotationVersionsById({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<Array<QuotationVersion>>> {
    return listQuotationVersionsById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotationId: quotationId,
        workspaceId: workspaceId,
    });
}

export async function getAdvancedQuotationById({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<AdvancedQuotation>> {
    const plutoResult = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/get",
        body: {
            id: quotationId,
        },
        responseValidator: plutoQuotationWithLineItemsZodValidator,
    });
    if (plutoResult.success !== true) {
        return plutoResult;
    }

    const emailByUserId = await loadWorkspaceUserEmailById({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
    });

    return dataResult(
        attachQuotationActorEmails(
            mapPlutoQuotationToAdvancedQuotation({
                lineItems: plutoResult.data.lineItems,
                plutoQuotation: plutoResult.data.quotation,
            }),
            emailByUserId,
        ),
    );
}

export async function createAdvancedQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return createNewAdvancedQuotation({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotation: quotation,
        workspaceId: workspaceId,
    });
}

export async function reviseAdvancedQuotation({
    accessToken,
    intellsysBaseUrl,
    quotation,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotation: LegacyQuotationWritePayload;
}): Promise<Result<Uuid>> {
    return plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/revise",
        body: buildPlutoReviseBodyFromLegacyPayload(quotation, {
            isAdvanced: true,
        }),
        responseValidator: uuidZodValidator,
    });
}

/**
 * Downloads the quotation as PDF. Triggers a file download in the browser.
 */
export async function downloadQuotationPdf({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<null>> {
    const pdfResult = await plutoFetchPdfBlob({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/download-pdf",
        body: {
            id: quotationId,
        },
    });
    if (pdfResult.success !== true) {
        return pdfResult;
    }

    triggerPdfBlobDownload(pdfResult.data.blob, quotationId, pdfResult.data.contentDisposition);
    return dataResult(null);
}

function pdfBlobToPreviewObjectUrl(blob: Blob): string {
    return URL.createObjectURL(blob);
}

export async function getQuotationPdfPreviewUrl({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<{url: string}>> {
    const pdfResult = await plutoFetchPdfBlob({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/generate-pdf-preview",
        body: {
            id: quotationId,
        },
    });
    if (pdfResult.success !== true) {
        return pdfResult;
    }

    return dataResult({
        url: pdfBlobToPreviewObjectUrl(pdfResult.data.blob),
    });
}

export async function getQuotationPdfPreviewUrlFromDraft({
    accessToken,
    draftQuotation,
    intellsysBaseUrl,
    isAdvanced = false,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    draftQuotation: LegacyQuotationWritePayload;
    isAdvanced?: boolean;
}): Promise<Result<{url: string}>> {
    const pdfResult = await plutoFetchPdfBlob({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/generate-pdf-preview-from-body",
        body: buildPlutoCreateBodyFromLegacyPayload(draftQuotation, {
            includeCatalogDisplayFields: true,
            isAdvanced: isAdvanced,
        }),
    });
    if (pdfResult.success !== true) {
        return pdfResult;
    }

    return dataResult({
        url: pdfBlobToPreviewObjectUrl(pdfResult.data.blob),
    });
}

/**
 * Advanced quotation PDF download — same Pluto endpoint as standard quotations.
 */
export async function downloadAdvancedQuotationPdf({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<null>> {
    return downloadQuotationPdf({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotationId: quotationId,
        workspaceId: workspaceId,
    });
}

export async function getAdvancedQuotationPdfPreviewUrl({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: string;
}): Promise<Result<{url: string}>> {
    return getQuotationPdfPreviewUrl({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotationId: quotationId,
        workspaceId: workspaceId,
    });
}

export async function getAdvancedQuotationPdfPreviewUrlFromDraft({
    accessToken,
    draftQuotation,
    intellsysBaseUrl,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    draftQuotation: LegacyQuotationWritePayload;
}): Promise<Result<{url: string}>> {
    return getQuotationPdfPreviewUrlFromDraft({
        accessToken: accessToken,
        draftQuotation: draftQuotation,
        intellsysBaseUrl: intellsysBaseUrl,
        isAdvanced: true,
        workspaceId: workspaceId,
    });
}

const shareQuotationViaWhatsappResponseZodValidator = zod.object({
    getGabsBody: zod.unknown().optional(),
    pdfPublicUrl: zod.string().optional(),
    to: zod.string().optional(),
});

export async function shareQuotationViaWhatsapp({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    phoneNumber,
    receiverName,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: Uuid;
    phoneNumber: string;
    receiverName: string;
}): Promise<Result<void>> {
    const result = await plutoPost({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        workspaceId: workspaceId,
        path: "/quotations/share-via-whatsapp",
        body: {
            quotationId: quotationId,
            phoneNumber: phoneNumber,
            receiverName: receiverName,
        },
        responseValidator: shareQuotationViaWhatsappResponseZodValidator,
    });
    if (result.success !== true) {
        return result;
    }
    return dataResult(undefined);
}

export async function shareAdvancedQuotationViaWhatsapp({
    accessToken,
    intellsysBaseUrl,
    quotationId,
    phoneNumber,
    receiverName,
    workspaceId,
}: PlutoWorkspaceClientContext & {
    quotationId: Uuid;
    phoneNumber: string;
    receiverName: string;
}): Promise<Result<void>> {
    return shareQuotationViaWhatsapp({
        accessToken: accessToken,
        intellsysBaseUrl: intellsysBaseUrl,
        quotationId: quotationId,
        phoneNumber: phoneNumber,
        receiverName: receiverName,
        workspaceId: workspaceId,
    });
}
